import { useQuery } from '@tanstack/react-query';
import { fetchActiveTournament } from '@/lib/network';
import { TEAM_CREATE_TOURNAMENT_CALENDAR_ID } from '@/pages/Teams/constants';

const ACTIVE_TOURNAMENT_QUERY_KEY = ['active-tournament-calendar'] as const;
const ACTIVE_TOURNAMENT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Resolves the tournament calendar id the public app should use for team
 * creation and the current matchday window.
 *
 * The id is configured in the admin panel by marking a tournament as "Active".
 * It is fetched at runtime so it can be changed without a frontend redeploy.
 * Falls back to the build-time constant if no active tournament is configured
 * or the request fails, so the app keeps working in every environment.
 */
export const useTeamCreateTournamentCalendarId = (): {
  id: string | null;
  format: string | null;
  hasResolved: boolean;
  isUsingFallback: boolean;
} => {
  const { data, isPending } = useQuery({
    queryKey: ACTIVE_TOURNAMENT_QUERY_KEY,
    queryFn: fetchActiveTournament,
    staleTime: ACTIVE_TOURNAMENT_STALE_TIME_MS,
    retry: 1,
  });

  const activeTournamentId = data?.data.id ?? null;

  if (activeTournamentId) {
    return {
      id: activeTournamentId,
      format: data?.data.format ?? null,
      hasResolved: true,
      isUsingFallback: false,
    };
  }

  if (isPending) {
    return {
      id: null,
      format: null,
      hasResolved: false,
      isUsingFallback: false,
    };
  }

  return {
    id: TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    format: data?.data.format ?? null,
    hasResolved: true,
    isUsingFallback: true,
  };
};
